import { FC, useState, useEffect } from 'react';
import { IconWithText } from '../icon-with-text/IconWithText';
import { Icons } from '../../atoms/icon/index';
import { Direction, Colors, Typographies, Sizes } from '../../../theme';
import { Spacing } from '../../../utils';
import { Typography } from '../../atoms';
import { ContainerV2 } from '../../atoms/containerV2/ContainerV2';
import { StyledContainer } from './RadioButton.styles';

type IconText = { [key in Sizes]: Typographies };

const iconTextMapping: IconText = {
  small: 'body2',
  medium: 'body2',
  large: 'h2',
};

const defaultSelectedIndex = 0;

type Color = {
  icon?: Colors;
  label?: Colors;
};

export type RadioButtonProps = {
  defaultSelected?: number;
  selectedIndex?: number;
  headerLabel?: string;
  labels: string[];
  size?: Sizes;
  onSelect: (selectedIndex: number) => void;
  direction?: Direction;
  activeColor?: Color;
  inactiveColor?: Color;
  padding?: Spacing;
};

export const RadioButton: FC<React.PropsWithChildren<RadioButtonProps>> = ({
  defaultSelected = defaultSelectedIndex,
  selectedIndex,
  headerLabel = '',
  labels,
  size = 'small',
  onSelect,
  direction = 'vertical',
  activeColor = { label: 'primary', icon: 'red' },
  inactiveColor = { label: 'primary', icon: 'primary' },
  padding,
}) => {
  const isControlled = selectedIndex !== undefined;

  const [internalSelected, setInternalSelected] = useState(() => {
    return defaultSelected >= labels.length ? defaultSelectedIndex : defaultSelected;
  });

  useEffect(() => {
    if (!isControlled) {
      setInternalSelected(defaultSelected >= labels.length ? defaultSelectedIndex : defaultSelected);
    }
  }, [defaultSelected, labels.length, isControlled]);

  // Use controlled selectedIndex if provided, else internal state
  const currentSelected = isControlled ? selectedIndex : internalSelected;

  const handleRadioButtonSelect = (index: number) => {
    if (!isControlled) {
      setInternalSelected(index);
    }
    onSelect(index);
  };

  const radioList = labels.map((label, index) => (
    <IconWithText
      iconOptions={{
        icon: currentSelected === index ? Icons.RADIOBUTTON_ACTIVE : Icons.RADIOBUTTON,
        size,
        role: 'radio',
        color: currentSelected === index ? activeColor.icon : inactiveColor.icon,
      }}
      textOptions={{
        type: iconTextMapping[size],
        color: currentSelected === index ? activeColor.label : inactiveColor.label,
      }}
      label={label}
      onClick={() => handleRadioButtonSelect(index)}
      key={label}
      padding={padding}
    />
  ));

  return (
    <ContainerV2 data-testid="radio-button-with-header">
      {headerLabel && <Typography margin={[0, 0, 2]}>{headerLabel}</Typography>}
      <StyledContainer role="radiogroup" direction={direction} data-testid="radio-button-container">
        {radioList}
      </StyledContainer>
    </ContainerV2>
  );
};
